import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

import { CandidatesList } from '@/components/CandidatesList';
import { ColumnMappingPanel } from '@/components/ColumnMappingPanel';
import { FiltersPanel, type FiltersState } from '@/components/FiltersPanel';
import { GraphView } from '@/components/GraphView';
import { HeroPanel } from '@/components/HeroPanel';
import { ImportPanel } from '@/components/ImportPanel';
import { LocationAliasPanel } from '@/components/LocationAliasPanel';
import { MapView } from '@/components/MapView';
import { RouteCard } from '@/components/RouteCard';
import { NormalizationReportPanel } from '@/components/NormalizationReportPanel';
import { StatsStrip } from '@/components/StatsStrip';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { forwardGeocode, getForwardGeocodeTaskState, hasGeoapifyApiKeyConfigured } from '@/features/geo/geocoding';
import { normalizeRowsWithAudit, parseWorkbookFile } from '@/features/import/parser';
import { effectiveLatency } from '@/features/routing/solver';
import { useRouteSolver } from '@/features/routing/useRouteSolver';
import type {
  ColumnMapping,
  GeocodeResult,
  LocationAliasMap,
  LocationGeoMap,
  ManualGeoOverrideMap,
  RouteCandidate,
  RouteMode,
} from '@/features/network/models';
import { uniq } from '@/features/import/normalizers';
import { downloadJson, downloadRoutesXlsx } from '@/lib/export';
import { useLocalStorageState } from '@/lib/storage';

const DEFAULT_FILTERS: FiltersState = {
  source: '',
  target: '',
  mode: 'balanced',
  maxHops: 4,
  minCapacityMbps: 0,
  includeUnknownLatency: true,
  amortizationMonths: 12,
  topK: 5,
  statusSelection: [],
  serviceTypeSelection: [],
  searchQuery: '',
  maxTotalLatencyMs: null,
  maxEffectiveMonthlyCost: null,
  avoidLocationsText: '',
  orderedWaypointsText: '',
  routeWeights: {
    latency: 50,
    cost: 30,
    hops: 20,
  },
};

const ROUTE_MODES: RouteMode[] = ['latency', 'cost', 'hops', 'balanced', 'pareto'];

const EMPTY_ROWS: Record<string, unknown>[] = [];
const EMPTY_HEADERS: string[] = [];
const EMPTY_MAPPING: ColumnMapping = {};
const IMPORT_STORAGE_KEY = 'network-route-planner:import';
const PAGE_STORAGE_KEY = 'network-route-planner:page';

type AppPage = 'import' | 'mapping' | 'normalization' | 'filter' | 'route-search';
type ViewMode = 'graph' | 'map';

const APP_PAGES: { id: AppPage; label: string }[] = [
  { id: 'import', label: 'Импорт' },
  { id: 'mapping', label: 'Мепинг и alias merge' },
  { id: 'normalization', label: 'Отчёт по нормализации' },
  { id: 'filter', label: 'Фильтр' },
  { id: 'route-search', label: 'Поиск маршрута' },
];

type PersistedImportState = {
  fileName: string;
  sheetNames: string[];
  rowsBySheet: Record<string, Record<string, unknown>[]>;
  headersBySheet: Record<string, string[]>;
  columnMappings: Record<string, ColumnMapping>;
  activeSheet: string;
};

function parseCommaSeparated(text: string) {
  return text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

const GEO_CACHE_STORAGE_KEY = 'geo:forward-geocode-cache';
const GEO_MANUAL_OVERRIDES_STORAGE_KEY = 'geo:manual-overrides';
const IS_GEOAPIFY_CONFIGURED = hasGeoapifyApiKeyConfigured();

function normalizeGeoQuery(input: string) {
  return input.trim().replace(/\s+/g, ' ').toLowerCase();
}

function readLocationGeoMap(locations: string[]): LocationGeoMap {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return {};
  }

  const normalizedToLocation = new Map<string, string>();
  for (const location of locations) {
    normalizedToLocation.set(normalizeGeoQuery(location), location);
  }

  try {
    const raw = window.localStorage.getItem(GEO_CACHE_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};

    const entries = parsed as Record<string, { value?: GeocodeResult }>;
    const result: LocationGeoMap = {};

    for (const entry of Object.values(entries)) {
      const value = entry?.value;
      if (!value || typeof value !== 'object') continue;

      const normalizedQuery = typeof value.normalizedQuery === 'string' ? value.normalizedQuery : '';
      if (!normalizedQuery) continue;

      const location = normalizedToLocation.get(normalizedQuery);
      if (!location) continue;

      if (!value.point || !Number.isFinite(value.point.lat) || !Number.isFinite(value.point.lon)) {
        continue;
      }

      result[location] = value;
    }

    return result;
  } catch {
    return {};
  }
}

function isValidCoordinate(value: unknown, min: number, max: number) {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function hasValidGeocodePoint(result: GeocodeResult | null | undefined) {
  if (!result || result.status !== 'ok' || !result.point) {
    return false;
  }

  return isValidCoordinate(result.point.lat, -90, 90) && isValidCoordinate(result.point.lon, -180, 180);
}

function readManualGeoOverrides(value: ManualGeoOverrideMap): ManualGeoOverrideMap {
  const result: ManualGeoOverrideMap = {};

  for (const [normalizedLocation, override] of Object.entries(value)) {
    if (!normalizedLocation || !override || typeof override !== 'object') continue;

    if (!isValidCoordinate(override.lat, -90, 90) || !isValidCoordinate(override.lon, -180, 180)) {
      continue;
    }

    result[normalizedLocation] = {
      lat: override.lat,
      lon: override.lon,
      updatedAt: typeof override.updatedAt === 'string' ? override.updatedAt : new Date(0).toISOString(),
    };
  }

  return result;
}

function buildManualOverrideLocationGeoMap(locations: string[], overrides: ManualGeoOverrideMap): LocationGeoMap {
  const result: LocationGeoMap = {};

  for (const location of locations) {
    const normalizedLocation = normalizeGeoQuery(location);
    const override = overrides[normalizedLocation];
    if (!override) continue;

    result[location] = {
      query: location,
      normalizedQuery: normalizedLocation,
      status: 'ok',
      point: { lat: override.lat, lon: override.lon },
      confidence: 1,
      provider: 'manual_override',
      displayName: location,
      cached: true,
      error: null,
    };
  }

  return result;
}

function normalizeFilters(filters: Partial<FiltersState> | null | undefined): FiltersState {
  const source = filters ?? {};
  const routeWeights: Partial<FiltersState['routeWeights']> = source.routeWeights ?? {};
  const mode = ROUTE_MODES.includes(source.mode as RouteMode) ? (source.mode as RouteMode) : DEFAULT_FILTERS.mode;

  const asNumber = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
  const asNullableNumber = (value: unknown, fallback: number | null) => {
    if (value == null) return null;
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  };

  return {
    ...DEFAULT_FILTERS,
    ...source,
    mode,
    maxHops: asNumber(source.maxHops, DEFAULT_FILTERS.maxHops),
    minCapacityMbps: asNumber(source.minCapacityMbps, DEFAULT_FILTERS.minCapacityMbps),
    includeUnknownLatency: typeof source.includeUnknownLatency === 'boolean' ? source.includeUnknownLatency : DEFAULT_FILTERS.includeUnknownLatency,
    amortizationMonths: asNumber(source.amortizationMonths, DEFAULT_FILTERS.amortizationMonths),
    topK: asNumber(source.topK, DEFAULT_FILTERS.topK),
    maxTotalLatencyMs: asNullableNumber(source.maxTotalLatencyMs, DEFAULT_FILTERS.maxTotalLatencyMs),
    maxEffectiveMonthlyCost: asNullableNumber(source.maxEffectiveMonthlyCost, DEFAULT_FILTERS.maxEffectiveMonthlyCost),
    statusSelection: Array.isArray(source.statusSelection) ? source.statusSelection : DEFAULT_FILTERS.statusSelection,
    serviceTypeSelection: Array.isArray(source.serviceTypeSelection) ? source.serviceTypeSelection : DEFAULT_FILTERS.serviceTypeSelection,
    searchQuery: typeof source.searchQuery === 'string' ? source.searchQuery : DEFAULT_FILTERS.searchQuery,
    avoidLocationsText: typeof source.avoidLocationsText === 'string' ? source.avoidLocationsText : DEFAULT_FILTERS.avoidLocationsText,
    orderedWaypointsText: typeof source.orderedWaypointsText === 'string' ? source.orderedWaypointsText : DEFAULT_FILTERS.orderedWaypointsText,
    routeWeights: {
      latency: asNumber(routeWeights.latency, DEFAULT_FILTERS.routeWeights.latency),
      cost: asNumber(routeWeights.cost, DEFAULT_FILTERS.routeWeights.cost),
      hops: asNumber(routeWeights.hops, DEFAULT_FILTERS.routeWeights.hops),
    },
  };
}

export default function App() {
  const [persistedImport, setPersistedImport] = useLocalStorageState<PersistedImportState | null>(IMPORT_STORAGE_KEY, null);
  const [activePage, setActivePage] = useLocalStorageState<AppPage>(PAGE_STORAGE_KEY, 'import');
  const [fileName, setFileName] = useState(() => persistedImport?.fileName ?? '');
  const [sheetNames, setSheetNames] = useState<string[]>(() => persistedImport?.sheetNames ?? []);
  const [rowsBySheet, setRowsBySheet] = useState<Record<string, Record<string, unknown>[]>>(() => persistedImport?.rowsBySheet ?? {});
  const [headersBySheet, setHeadersBySheet] = useState<Record<string, string[]>>(() => persistedImport?.headersBySheet ?? {});
  const [columnMappings, setColumnMappings] = useState<Record<string, ColumnMapping>>(() => persistedImport?.columnMappings ?? {});
  const [activeSheet, setActiveSheet] = useState(() => persistedImport?.activeSheet ?? '');
  const [importError, setImportError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [graphLayoutRerunVersion, setGraphLayoutRerunVersion] = useState(0);
  const [filters, setFilters] = useLocalStorageState<FiltersState>('network-route-planner:filters', DEFAULT_FILTERS);
  const [locationAliases, setLocationAliases] = useLocalStorageState<LocationAliasMap>('network-route-planner:aliases', {});
  const [manualGeoOverrides, setManualGeoOverrides] = useLocalStorageState<ManualGeoOverrideMap>(GEO_MANUAL_OVERRIDES_STORAGE_KEY, {});
  const [locationGeoMap, setLocationGeoMap] = useState<LocationGeoMap>({});
  const [geocodingProgress, setGeocodingProgress] = useState({ total: 0, completed: 0, running: false });
  const [selectedRouteForGraphId, setSelectedRouteForGraphId] = useState<string | null>(null);
  const safeFilters = useMemo(() => normalizeFilters(filters), [filters]);
  const safeManualGeoOverrides = useMemo(() => readManualGeoOverrides(manualGeoOverrides), [manualGeoOverrides]);

  useEffect(() => {
    setFilters((previous) => normalizeFilters(previous));
  }, [setFilters]);

  useEffect(() => {
    if (!fileName || sheetNames.length === 0) {
      setPersistedImport(null);
      return;
    }

    setPersistedImport({
      fileName,
      sheetNames,
      rowsBySheet,
      headersBySheet,
      columnMappings,
      activeSheet,
    });
  }, [activeSheet, columnMappings, fileName, headersBySheet, rowsBySheet, setPersistedImport, sheetNames]);

  async function handleUpload(file: File | null) {
    if (!file) {
      setFileName('');
      setSheetNames([]);
      setRowsBySheet({});
      setHeadersBySheet({});
      setColumnMappings({});
      setActiveSheet('');
      setImportError(null);
      setPersistedImport(null);
      return;
    }

    try {
      const parsed = await parseWorkbookFile(file);
      setFileName(parsed.fileName);
      setSheetNames(parsed.sheetNames);
      setRowsBySheet(parsed.rowsBySheet);
      setHeadersBySheet(parsed.headersBySheet);
      setColumnMappings(parsed.inferredMappingsBySheet);
      setActiveSheet(parsed.sheetNames[0] || '');
      setImportError(null);
      setFilters((prev) => ({ ...prev, source: '', target: '' }));
      setActivePage('filter');
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Не удалось разобрать файл');
    }
  }

  const activeRows = rowsBySheet[activeSheet] ?? EMPTY_ROWS;
  const activeHeaders = headersBySheet[activeSheet] ?? EMPTY_HEADERS;
  const activeMapping = columnMappings[activeSheet] ?? EMPTY_MAPPING;
  const normalizationReport = useMemo(() => normalizeRowsWithAudit(activeRows, activeMapping, locationAliases), [activeRows, activeMapping, locationAliases]);
  const edges = normalizationReport.graphEdges;

  const locations = useMemo(() => uniq(edges.flatMap((edge) => [edge.from, edge.to])).sort(), [edges]);
  const manualOverrideGeoMap = useMemo(
    () => buildManualOverrideLocationGeoMap(locations, safeManualGeoOverrides),
    [locations, safeManualGeoOverrides],
  );
  const effectiveLocationGeoMap = useMemo(
    () => ({ ...locationGeoMap, ...manualOverrideGeoMap }),
    [locationGeoMap, manualOverrideGeoMap],
  );
  const statuses = useMemo(() => uniq(edges.map((edge) => edge.status).filter(Boolean)).sort(), [edges]);
  const serviceTypes = useMemo(() => uniq(edges.map((edge) => edge.serviceType).filter(Boolean)).sort(), [edges]);
  const filteredEdges = useMemo(() => {
    return edges.filter((edge) => {
      const statusPass = safeFilters.statusSelection.length === 0 || safeFilters.statusSelection.includes(edge.status);
      const serviceTypePass =
        safeFilters.serviceTypeSelection.length === 0 || safeFilters.serviceTypeSelection.includes(edge.serviceType);

      return statusPass && serviceTypePass;
    });
  }, [edges, safeFilters.statusSelection, safeFilters.serviceTypeSelection]);

  useEffect(() => {
    setLocationGeoMap((previous) => {
      const cachedGeoMap = readLocationGeoMap(locations);
      const next: LocationGeoMap = {};

      for (const location of locations) {
        if (cachedGeoMap[location]) {
          next[location] = cachedGeoMap[location];
          continue;
        }

        if (previous[location]) {
          next[location] = previous[location];
        }
      }

      return next;
    });
  }, [locations]);

  useEffect(() => {
    if (locations.length === 0) {
      setGeocodingProgress({ total: 0, completed: 0, running: false });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    let runTimer: number | null = null;
    const cachedGeoMap = readLocationGeoMap(locations);
    const queue = locations.filter((location) => {
      const normalizedLocation = normalizeGeoQuery(location);
      if (safeManualGeoOverrides[normalizedLocation]) {
        return false;
      }

      const geocodeTaskState = getForwardGeocodeTaskState(location);
      if (geocodeTaskState === 'in_flight' || geocodeTaskState === 'cooldown') {
        return false;
      }

      return !hasValidGeocodePoint(cachedGeoMap[location]);
    });

    const autoGeocodingQueue = IS_GEOAPIFY_CONFIGURED ? queue : [];

    setGeocodingProgress({
      total: autoGeocodingQueue.length,
      completed: 0,
      running: autoGeocodingQueue.length > 0,
    });

    if (autoGeocodingQueue.length === 0) {
      return () => {
        controller.abort();
        cancelled = true;
      };
    }

    const run = async () => {
      let completed = 0;

      for (const location of autoGeocodingQueue) {
        if (cancelled) {
          break;
        }

        try {
          const result = await forwardGeocode(location, {}, controller.signal);
          if (cancelled) {
            break;
          }

          setLocationGeoMap((previous) => {
            const normalizedLocation = normalizeGeoQuery(location);
            if (safeManualGeoOverrides[normalizedLocation]) {
              return previous;
            }

            return {
              ...previous,
              [location]: result,
            };
          });
        } catch {
          if (cancelled) {
            break;
          }
        }

        completed += 1;
        setGeocodingProgress({
          total: autoGeocodingQueue.length,
          completed,
          running: completed < autoGeocodingQueue.length,
        });
      }
    };

    runTimer = window.setTimeout(() => {
      if (!cancelled) {
        void run();
      }
    }, 0);

    return () => {
      cancelled = true;
      if (runTimer != null) {
        window.clearTimeout(runTimer);
      }
      controller.abort();
    };
  }, [locations, safeManualGeoOverrides]);

  const visibleLocations = useMemo(() => {
    if (!safeFilters.searchQuery) return locations.slice(0, 250);
    const query = safeFilters.searchQuery.toLowerCase();
    return locations.filter((location) => location.toLowerCase().includes(query)).slice(0, 250);
  }, [safeFilters.searchQuery, locations]);

  const stats = useMemo(() => {
    const latencies = filteredEdges.map(effectiveLatency).filter((value): value is number => value != null);
    const capacities = filteredEdges.map((edge) => edge.capacityMbps).filter((value): value is number => value != null);
    const filteredStatuses = uniq(filteredEdges.map((edge) => edge.status).filter(Boolean));
    const filteredVendors = uniq(filteredEdges.map((edge) => edge.vendorName).filter(Boolean));

    return {
      rows: activeRows.length,
      edges: filteredEdges.length,
      nodes: uniq(filteredEdges.flatMap((edge) => [edge.from, edge.to])).length,
      statuses: filteredStatuses.length,
      vendors: filteredVendors.length,
      avgLatency: latencies.length ? latencies.reduce((acc, value) => acc + value, 0) / latencies.length : null,
      maxCapacity: capacities.length ? Math.max(...capacities) : null,
    };
  }, [activeRows.length, filteredEdges]);

  const solverOptions = useMemo(() => ({
    source: safeFilters.source,
    target: safeFilters.target,
    mode: safeFilters.mode,
    maxHops: safeFilters.maxHops,
    minCapacityMbps: safeFilters.minCapacityMbps,
    allowedStatuses: safeFilters.statusSelection,
    serviceTypeFilter: safeFilters.serviceTypeSelection,
    includeUnknownLatency: safeFilters.includeUnknownLatency,
    amortizationMonths: safeFilters.amortizationMonths,
    topK: safeFilters.topK,
    maxTotalLatencyMs: safeFilters.maxTotalLatencyMs,
    maxEffectiveMonthlyCost: safeFilters.maxEffectiveMonthlyCost,
    avoidLocations: parseCommaSeparated(safeFilters.avoidLocationsText),
    orderedWaypoints: parseCommaSeparated(safeFilters.orderedWaypointsText),
    routeWeights: safeFilters.routeWeights,
  }), [safeFilters]);

  const { primary, backup, routes, loading, error, progress } = useRouteSolver(filteredEdges, solverOptions);
  const selectedRouteForGraph = useMemo<RouteCandidate | null>(
    () => routes.find((route) => route.id === selectedRouteForGraphId) ?? null,
    [routes, selectedRouteForGraphId],
  );

  useEffect(() => {
    if (selectedRouteForGraphId && !routes.some((route) => route.id === selectedRouteForGraphId)) {
      setSelectedRouteForGraphId(null);
    }
  }, [routes, selectedRouteForGraphId]);

  function patchFilters(patch: Partial<FiltersState> | ((previous: FiltersState) => FiltersState)) {
    if (typeof patch === 'function') {
      setFilters((previous) => normalizeFilters(patch(normalizeFilters(previous))));
      return;
    }
    setFilters((previous) => normalizeFilters({ ...normalizeFilters(previous), ...patch }));
  }

  return (
    <div className="min-h-screen w-full p-4 md:p-6">
      <div className="w-full space-y-6">
        <HeroPanel rows={stats.rows} edges={stats.edges} nodes={stats.nodes} maxCapacity={stats.maxCapacity} />

        <Card className="p-3">
          <div className="flex flex-wrap gap-2">
            {APP_PAGES.map((page) => (
              <Button
                key={page.id}
                type="button"
                variant={activePage === page.id ? 'primary' : 'secondary'}
                onClick={() => setActivePage(page.id)}
              >
                {page.label}
              </Button>
            ))}
          </div>
        </Card>

        {importError ? (
          <Card className="p-4 text-sm text-rose-700 dark:text-rose-300">Ошибка импорта: {importError}</Card>
        ) : null}
        {error && activePage === 'route-search' ? (
          <Card className="p-4 text-sm text-rose-700 dark:text-rose-300">Ошибка расчёта: {error}</Card>
        ) : null}

        {activePage === 'import' ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <ImportPanel
              fileName={fileName}
              sheetNames={sheetNames}
              activeSheet={activeSheet}
              mode={safeFilters.mode}
              onUpload={handleUpload}
              onSheetChange={setActiveSheet}
              onModeChange={(value) => patchFilters({ mode: value as RouteMode })}
            />
          </motion.div>
        ) : null}

        {activePage === 'mapping' ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-6">
            {activeHeaders.length > 0 ? (
              <ColumnMappingPanel
                headers={activeHeaders}
                mapping={activeMapping}
                previewRows={activeRows}
                onChange={(next) => setColumnMappings((prev) => ({ ...prev, [activeSheet]: next }))}
              />
            ) : (
              <Card className="p-6 text-sm text-slate-600 dark:text-slate-300">Сначала загрузи файл и выбери лист для настройки мепинга.</Card>
            )}

            {locations.length > 0 ? (
              <LocationAliasPanel
                locations={locations}
                aliases={locationAliases}
                onChange={setLocationAliases}
                geoOverrides={safeManualGeoOverrides}
                onGeoOverridesChange={setManualGeoOverrides}
              />
            ) : (
              <Card className="p-6 text-sm text-slate-600 dark:text-slate-300">Alias merge станет доступен после нормализации данных.</Card>
            )}
          </motion.div>
        ) : null}

        {activePage === 'normalization' ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            {activeRows.length > 0 ? (
              <NormalizationReportPanel report={normalizationReport} />
            ) : (
              <Card className="p-6 text-sm text-slate-600 dark:text-slate-300">Нет данных для отчёта. Загрузи файл на странице импорта.</Card>
            )}
          </motion.div>
        ) : null}

        {activePage === 'filter' ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
            <FiltersPanel
              locations={locations}
              visibleLocations={visibleLocations}
              statuses={statuses}
              serviceTypes={serviceTypes}
              state={safeFilters}
              onStateChange={(patch) => patchFilters(patch)}
              onReset={() => patchFilters({ statusSelection: [], serviceTypeSelection: [] })}
              onRerunLayout={() => setGraphLayoutRerunVersion((previous) => previous + 1)}
              showRouteControls={false}
              showDatasetFilters
            />

            <div className="space-y-6">
              <StatsStrip avgLatency={stats.avgLatency} statuses={stats.statuses} vendors={stats.vendors} routes={routes.length} />

              <Card className="p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant={viewMode === 'graph' ? 'primary' : 'secondary'} onClick={() => setViewMode('graph')}>
                      Graph
                    </Button>
                    <Button type="button" variant={viewMode === 'map' ? 'primary' : 'secondary'} onClick={() => setViewMode('map')}>
                      Map
                    </Button>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {geocodingProgress.running || geocodingProgress.total > 0
                      ? `Геокодинг: ${geocodingProgress.completed}/${geocodingProgress.total}`
                      : 'Геокодинг завершён'}
                  </div>
                </div>
              </Card>

              {viewMode === 'graph' ? (
                <GraphView edges={filteredEdges} primary={primary} backup={backup} layoutRerunVersion={graphLayoutRerunVersion} />
              ) : (
                <MapView edges={filteredEdges} primary={primary} backup={backup} locationGeoMap={effectiveLocationGeoMap} />
              )}
            </div>
          </motion.div>
        ) : null}

        {activePage === 'route-search' ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
            <FiltersPanel
              locations={locations}
              visibleLocations={visibleLocations}
              statuses={statuses}
              serviceTypes={serviceTypes}
              state={safeFilters}
              onStateChange={(patch) => patchFilters(patch)}
              onReset={() => setFilters(DEFAULT_FILTERS)}
              showRouteControls
              showDatasetFilters={false}
            />

            <div className="space-y-6">
              <StatsStrip avgLatency={stats.avgLatency} statuses={stats.statuses} vendors={stats.vendors} routes={routes.length} />

              {loading || progress.running ? (
                <Card className="p-4">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Идёт расчёт маршрутов…</div>
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {progress.message} · {progress.percent}%
                  </div>
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-slate-900 transition-[width] duration-200 ease-out dark:bg-slate-100"
                      style={{ width: `${Math.max(2, Math.min(100, progress.percent))}%` }}
                    />
                  </div>
                </Card>
              ) : null}

              <Card className="p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant={viewMode === 'graph' ? 'primary' : 'secondary'} onClick={() => setViewMode('graph')}>
                      Graph
                    </Button>
                    <Button type="button" variant={viewMode === 'map' ? 'primary' : 'secondary'} onClick={() => setViewMode('map')}>
                      Map
                    </Button>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {geocodingProgress.running || geocodingProgress.total > 0
                      ? `Геокодинг: ${geocodingProgress.completed}/${geocodingProgress.total}`
                      : 'Геокодинг завершён'}
                  </div>
                </div>
              </Card>

              {viewMode === 'graph' ? (
                <GraphView
                  edges={filteredEdges}
                  primary={selectedRouteForGraph ?? primary}
                  backup={selectedRouteForGraph ? null : backup}
                />
              ) : (
                <MapView edges={filteredEdges} primary={primary} backup={backup} locationGeoMap={effectiveLocationGeoMap} />
              )}

              <div className="grid gap-6 2xl:grid-cols-2">
                <RouteCard title="Основной маршрут" route={primary} primary />
                <RouteCard title="Резервный маршрут" route={backup} />
              </div>

              <CandidatesList
                routes={routes}
                loading={loading}
                progress={progress}
                selectedRouteId={selectedRouteForGraphId}
                onSelectRouteForGraph={(route) => setSelectedRouteForGraphId(route.id)}
                onExportJson={() => downloadJson('routes.json', routes)}
                onExportXlsx={() => downloadRoutesXlsx('routes.xlsx', routes)}
              />
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
